// Copyright (c) 2025 Just Feasible Solutions LLC. All rights reserved.
// This file is part of the Just Feasible Solutions application.
// Unauthorized copying, distribution, or modification of this file is prohibited.
// For license information, contact the author.

import React from 'react';

const Privacy = () => (
  <div style={{ maxWidth: 900, margin: '0 auto', padding: '2rem' }}>
    <h1>Privacy Policy</h1>
    <p><b>Effective as of: January 10, 2025</b></p>
    <p>
      We value your trust and respect your privacy. We exist to support small business owners like you by offering a fast and easy way for you to manage your business finances.
      We believe the only good policy is one you can understand without a lawyer by your side, so we’ve worked hard to explain—clearly and in plain English—what we do, what information we collect, and why, so you can feel confident about using our services.
      If anything here is unclear, please reach out to us. We sincerely hope this helps you better understand our privacy practices, and how we operate.
    </p>
    <h2>A. Scope</h2>
    <h3>Children's Privacy & Parental Consent</h3>
    <p>
      <b>Our services are not intended for children under the age of 13.</b> We do not knowingly collect, use, or disclose personal information from children under 13 without verifiable parental consent. If a child under 13 wishes to use our services, we require a parent or legal guardian to provide consent before any personal information is collected.
    </p>
    <p>
      If we become aware that we have collected personal information from a child under 13 without parental consent, we will take steps to delete that information as soon as possible. Parents or guardians who believe that their child has provided us with personal information without their consent should contact us at <a href="mailto:support@justfeasible.com">support@justfeasible.com</a>.
    </p>
    <p>
      To provide parental consent, parents or legal guardians may be required to complete a consent form or provide other verifiable means of consent as permitted by law. We will provide instructions and support for this process upon request.
    </p>
    <h3>In Simple Terms</h3>
    <ul>
      <li>This privacy policy does not apply to products or services offered by third parties on or through our site or services. Third parties have their own privacy policies.</li>
      <li>We do not market our services to children under the age of 13, nor do we knowingly collect information from children under the age of 13.</li>
      <li>"Personal Information" is data that can be used to identify a person individually. This policy covers our collection, protection, use, retention, disclosure, and other processing of Personal Information and your rights relating to these activities.</li>
    </ul>
    <h3>Categories of Personal Information Collected</h3>
    <ul>
      <li>Contact Information (e.g., name, phone number, address, and email address)</li>
      <li>Government identification numbers (e.g., Social Security Number, Employer Identification Number)</li>
      <li>Date of Birth</li>
      <li>Financial Information (e.g., income, revenue, assets, credits, deductions, expenses, and bank account information)</li>
      <li>Payment Data (e.g., checking, debit and credit card account numbers, balances and payment history)</li>
      <li>Health Information (e.g., health insurance status and financial information related to payment for healthcare services)</li>
      <li>Geo-Location Information</li>
      <li>Website, Mobile Application, and Email Usage Data</li>
      <li>Device Information</li>
      <li>Login Information</li>
      <li>Demographic, professional, and education information</li>
    </ul>
    <h3>How We Use Your Information</h3>
    <ul>
      <li>To provide you with the products and services you request</li>
      <li>To service and report on your account</li>
      <li>To offer you relevant information, products, or services</li>
      <li>To enhance our products and services</li>
      <li>To improve the security of our services and protect your identity</li>
      <li>To comply with legal obligations</li>
    </ul>
    <h3>How We Disclose Information</h3>
    <ul>
      <li>We do not sell your personal information to other companies.</li>
      <li>We may share information with affiliates, business partners, and service providers as needed to provide our services or as required by law.</li>
      <li>We may disclose information to government authorities when legally required.</li>
      <li>We may share aggregate or anonymized data that cannot identify you personally.</li>
    </ul>
    <h3>Your Choices</h3>
    <ul>
      <li>You may request access to, correction, or deletion of your personal information.</li>
      <li>You may opt out of marketing communications at any time.</li>
      <li>You may manage your cookie preferences in your browser settings.</li>
    </ul>
    <h3>Security</h3>
    <ul>
      <li>We use administrative, technical, and physical safeguards to protect your information.</li>
      <li>No method of transmission over the Internet or electronic storage is 100% secure, so we cannot guarantee absolute security.</li>
    </ul>
    <h3>Advertising and Analytics</h3>
    <p>
      Our app uses Google Mobile Ads (AdMob) to display advertisements. As part of this, the app and its third-party partners may collect and use your device’s Advertising ID for ad personalization and analytics purposes. The Advertising ID is a unique, user-resettable ID provided by Google Play services.
    </p>
    <p>
      You can opt out of personalized ads at any time by adjusting your device settings:
    </p>
    <ul>
      <li><b>On Android:</b> Settings &gt; Google &gt; Ads &gt; Opt out of Ads Personalization</li>
    </ul>
    <p>
      For more information on how Google uses data, please visit: <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">https://policies.google.com/technologies/ads</a>
    </p>
    <h3>Contact</h3>
    <p>If you have any questions or concerns about our privacy practices or this privacy policy, please contact us at <a href="mailto:support@justfeasible.com">support@justfeasible.com</a>.</p>
    <p>This policy may be updated from time to time. Please review it periodically for changes.</p>
    <p><i>This privacy policy is adapted for Just Feasible Solutions and is inspired by best practices in the industry.</i></p>
  </div>
);

export default Privacy;
